(() => {
  'use strict';

  const routeRoot = window.Shopify?.routes?.root || '/';

  // Keep custom hard-coded content links inside the active Shopify locale/market.
  if (routeRoot !== '/') {
    const safePrefixes = ['/products/', '/collections/', '/pages/', '/blogs/', '/search'];
    document.querySelectorAll('a[href^="/"]').forEach((link) => {
      const raw = link.getAttribute('href');
      if (!raw || raw.startsWith('//') || raw.startsWith(routeRoot)) return;
      if (!safePrefixes.some((prefix) => raw === prefix.slice(0, -1) || raw.startsWith(prefix))) return;
      link.setAttribute('href', routeRoot + raw.replace(/^\/+/, ''));
    });
  }

  const root = document.querySelector('[data-mbf-product-root]');
  const modelNode = root?.querySelector('[data-mbf-product-variant-model]');
  const picker = root?.querySelector('.variant-picker');
  const grid = picker?.querySelector('.variant-grid');
  const productHandle = String(root?.dataset?.mbfProductHandle || '').toLowerCase();

  if (!root || !modelNode || !picker || !grid) return;

  try {
    const model = JSON.parse(modelNode.textContent || '{}');
    const options = Array.isArray(model.options) ? model.options : [];
    const variants = Array.isArray(model.variants) ? model.variants : [];
    const originalButtons = [...grid.querySelectorAll('[data-mbf-variant]')];
    if (!options.length || variants.length <= 1 || !originalButtons.length) return;

    const selectedId = String(root.querySelector('input[name="id"]')?.value || model.selectedId || '');
    let current = variants.find((variant) => String(variant.id) === selectedId)
      || variants.find((variant) => variant.available)
      || variants[0];
    let selected = [...(current.options || [])];

    const normalize = (value) => String(value || '').toLocaleLowerCase();
    const priority = (name) => {
      const n = normalize(name);
      if (/(grootte|size|weight|gewicht|maat|größe|groesse|poids|taille|وزن|اندازه|الحجم|الوزن)/i.test(n)) return 0;
      if (/(smaak|flavou?r|geschmack|saveur|طعم|نكهة)/i.test(n)) return 1;
      return 2;
    };

    const ordered = options
      .map((name, index) => ({ name, index, priority: priority(name) }))
      .sort((a, b) => a.priority - b.priority || a.index - b.index);

    const hasExplicitVariant = (() => {
      try { return new URL(window.location.href).searchParams.has('variant'); }
      catch (_) { return false; }
    })();

    if (productHandle === 'iso-zero' && !hasExplicitVariant) {
      const sizeOption = ordered.find((item) => item.priority === 0);
      const preferredTwoKg = sizeOption ? variants.find((variant) => {
        const value = String(variant.options?.[sizeOption.index] || '').trim().toLowerCase();
        return variant.available && /^2\s*kg$/.test(value);
      }) : null;
      if (preferredTwoKg?.options) {
        current = preferredTwoKg;
        selected = [...preferredTwoKg.options];
      }
    }

    const positionByIndex = new Map(ordered.map((item, position) => [item.index, position]));
    const host = document.createElement('div');
    host.className = 'mbf-option-selector';
    host.setAttribute('aria-label', 'Product options');

    const exactVariant = () => variants.find((variant) =>
      Array.isArray(variant.options) &&
      variant.options.length === selected.length &&
      variant.options.every((value, index) => value === selected[index])
    );

    const matchesPreviousSelections = (variant, displayPosition) => {
      return ordered.slice(0, displayPosition).every(({ index }) => variant.options?.[index] === selected[index]);
    };

    const combinationExists = (optionIndex, value) => {
      const displayPosition = positionByIndex.get(optionIndex) ?? optionIndex;
      return variants.some((variant) =>
        Array.isArray(variant.options) &&
        variant.options[optionIndex] === value &&
        matchesPreviousSelections(variant, displayPosition)
      );
    };

    const canUseValue = (optionIndex, value) => {
      const displayPosition = positionByIndex.get(optionIndex) ?? optionIndex;
      return variants.some((variant) =>
        variant.available &&
        Array.isArray(variant.options) &&
        variant.options[optionIndex] === value &&
        matchesPreviousSelections(variant, displayPosition)
      );
    };

    const findAvailableVariant = (optionIndex, value) => {
      const displayPosition = positionByIndex.get(optionIndex) ?? optionIndex;
      return variants.find((variant) =>
        variant.available &&
        Array.isArray(variant.options) &&
        variant.options[optionIndex] === value &&
        matchesPreviousSelections(variant, displayPosition)
      );
    };

    const syncOriginalVariant = () => {
      const variant = exactVariant();
      if (!variant) return;
      const button = originalButtons.find((item) => String(item.dataset.variantId || '') === String(variant.id));
      if (button && !button.disabled) button.click();
    };

    const notifyLabel = root.dataset.mbfStockNotifyLabel || 'Notify me when available';
    const notifyMessage = root.dataset.mbfStockNotifyMessage || 'Hi MBF, please let me know when this is available again:';
    const whatsappLink = root.querySelector('.mbf-product-whatsapp');

    const openStockNotify = (optionIndex, value) => {
      const displayPosition = positionByIndex.get(optionIndex) ?? optionIndex;
      const targetSelected = [...selected];
      targetSelected[optionIndex] = value;

      const matchingUnavailable = variants.find((variant) =>
        Array.isArray(variant.options) &&
        variant.options[optionIndex] === value &&
        ordered.slice(0, displayPosition).every(({ index }) => variant.options?.[index] === targetSelected[index])
      );

      if (matchingUnavailable?.options) {
        matchingUnavailable.options.forEach((item, index) => {
          if (item) targetSelected[index] = item;
        });
      }

      const details = ordered
        .map(({ name, index }) => targetSelected[index] ? `${name}: ${targetSelected[index]}` : '')
        .filter(Boolean)
        .join(' · ');

      const fallbackBase = 'https://wa.me/31614958590';
      let base = fallbackBase;
      try {
        if (whatsappLink?.href) {
          const currentUrl = new URL(whatsappLink.href);
          base = `${currentUrl.origin}${currentUrl.pathname}`;
        }
      } catch (_) {}

      const message = `${notifyMessage} ${details}`.trim();
      window.open(`${base}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
    };

    const refreshAll = () => {
      host.querySelectorAll('.mbf-option-value').forEach((button) => {
        const idx = Number(button.dataset.optionIndex);
        const val = button.dataset.optionValue || '';
        const active = selected[idx] === val;
        const relevant = combinationExists(idx, val);
        const available = relevant && canUseValue(idx, val);
        button.hidden = !relevant;
        button.classList.toggle('is-selected', active && relevant);
        button.classList.toggle('is-unavailable', !available);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        button.setAttribute('aria-disabled', 'false');
        button.setAttribute('aria-label', available ? val : `${val} — ${notifyLabel}`);
        const note = button.querySelector('.mbf-option-value__note');
        if (note) {
          note.hidden = available;
          note.textContent = notifyLabel;
        }
      });
    };

    ordered.forEach(({ name: optionName, index: optionIndex }) => {
      const values = [...new Set(variants.map((variant) => variant.options?.[optionIndex]).filter(Boolean))];
      const group = document.createElement('div');
      group.className = 'mbf-option-group';
      group.dataset.optionIndex = String(optionIndex);

      const label = document.createElement('div');
      label.className = 'mbf-option-group__label';
      label.textContent = String(optionName);

      const valueHost = document.createElement('div');
      valueHost.className = 'mbf-option-group__values';

      values.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mbf-option-value';
        button.dataset.optionIndex = String(optionIndex);
        button.dataset.optionValue = value;

        const main = document.createElement('span');
        main.className = 'mbf-option-value__main';
        main.textContent = value;

        const note = document.createElement('small');
        note.className = 'mbf-option-value__note';
        note.hidden = true;
        note.textContent = notifyLabel;

        button.append(main, note);
        button.addEventListener('click', () => {
          if (!canUseValue(optionIndex, value)) {
            openStockNotify(optionIndex, value);
            return;
          }

          selected[optionIndex] = value;
          const direct = exactVariant();

          if (!direct?.available) {
            const fallback = findAvailableVariant(optionIndex, value);
            if (fallback?.options) selected = [...fallback.options];
          }

          refreshAll();
          syncOriginalVariant();
        });

        valueHost.appendChild(button);
      });

      group.append(label, valueHost);
      host.appendChild(group);
    });

    grid.before(host);
    picker.classList.add('mbf-variant-enhanced');
    refreshAll();
    if (productHandle === 'iso-zero' && !hasExplicitVariant) syncOriginalVariant();

    originalButtons.forEach((button) => button.addEventListener('click', () => {
      const variant = variants.find((item) => String(item.id) === String(button.dataset.variantId || ''));
      if (!variant) return;
      selected = [...variant.options];
      refreshAll();
    }));
  } catch (error) {
    console.warn('[MBF product option enhancer]', error);
  }
})();